import React, { useState, useEffect } from 'react'
import {
  Search,
  Filter,
  Calendar,
  DollarSign,
  Clock,
  CheckCircle,
  X,
  ChevronDown,
  Star,
  Instagram,
  ExternalLink,
  RefreshCw,
  AlertCircle
} from 'lucide-react'
import { useAuth } from '../../App'
import { mockOpportunities } from '../../data/mockData'

const demoOpportunities = [
  ...mockOpportunities,
  {
    id: '3',
    campaignName: 'Digital Health Innovation Summit',
    brand: {
      name: 'TechMed Conference',
      logo: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=100&h=100&fit=crop',
      verified: true,
    },
    budget: '$8,000 - $12,000',
    deadline: '2026-03-01',
    platforms: ['youtube', 'linkedin'],
    deliverables: ['1 Long-form Video', '2 Posts'],
    status: 'new',
    matchScore: 92,
    brief: 'Present the latest innovations in digital health at our virtual conference...',
    isDemo: true,
  },
  {
    id: '4',
    campaignName: 'Chronic Pain Awareness',
    brand: {
      name: 'PainRelief Medical',
      logo: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=100&h=100&fit=crop',
      verified: true,
    },
    budget: '$4,000 - $6,000',
    deadline: '2026-02-28',
    platforms: ['instagram', 'tiktok'],
    deliverables: ['3 Reels', '5 Stories'],
    status: 'new',
    matchScore: 85,
    brief: 'Educate audiences about chronic pain management options...',
    isDemo: true,
  },
]

function CreatorOpportunities() {
  const { user } = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedOpp, setSelectedOpp] = useState(null)
  const [filter, setFilter] = useState('all')
  const [opportunities, setOpportunities] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isResponding, setIsResponding] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Fetch real invitations for this creator
  useEffect(() => {
    fetchOpportunities()
  }, [user])

  const fetchOpportunities = async () => {
    setIsLoading(true)
    setError('')

    try {
      // Fetch invitations for current user
      const response = await fetch(`/api/invitations?creatorUserId=${user?.id}`)
      const data = await response.json()

      // Transform invitations to opportunity format
      const realOpps = (data.invitations || []).map(inv => ({
        id: inv.id,
        invitationId: inv.id,
        campaignName: inv.campaignName,
        brand: {
          name: inv.brandName,
          logo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=100&h=100&fit=crop',
          verified: true,
        },
        budget: inv.budget,
        deadline: inv.deadline,
        platforms: inv.platforms || [],
        deliverables: inv.deliverables || [],
        status: inv.status === 'pending' ? 'new' : inv.status,
        matchScore: inv.matchScore,
        brief: inv.brief,
        isReal: true,
      }))

      // Combine with demo opportunities (marked as demo)
      const allOpps = [...realOpps, ...demoOpportunities.map(opp => ({ ...opp, isDemo: true }))]

      setOpportunities(allOpps)
    } catch (err) {
      console.error('Failed to fetch opportunities:', err)
      // Use demo data as fallback
      setOpportunities(demoOpportunities.map(opp => ({ ...opp, isDemo: true })))
    } finally {
      setIsLoading(false)
    }
  }

  const filteredOpps = opportunities.filter(opp => {
    const matchesSearch = opp.campaignName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         opp.brand.name.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesFilter = filter === 'all' ||
                         (filter === 'new' && (opp.status === 'new' || opp.status === 'pending')) ||
                         (filter === 'pending_response' && opp.status === 'pending_response')
    return matchesSearch && matchesFilter
  })

  const handleAccept = async (opp) => {
    if (opp.isDemo) {
      alert(`You've expressed interest in "${opp.campaignName}"! (Demo opportunity)`)
      setSelectedOpp(null)
      return
    }

    setIsResponding(true)
    setError('')
    setSuccess('')

    try {
      const response = await fetch('/api/invitations', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invitationId: opp.invitationId,
          status: 'accepted',
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to accept opportunity')
      }

      setSuccess(`You've accepted "${opp.campaignName}"! The brand will be notified.`)
      setSelectedOpp(null)
      fetchOpportunities() // Refresh
    } catch (err) {
      setError(err.message || 'Failed to accept opportunity')
    } finally {
      setIsResponding(false)
    }
  }

  const handleDecline = async (opp) => {
    if (opp.isDemo) {
      alert(`You've declined "${opp.campaignName}". (Demo opportunity)`)
      setSelectedOpp(null)
      return
    }

    setIsResponding(true)
    setError('')

    try {
      const response = await fetch('/api/invitations', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invitationId: opp.invitationId,
          status: 'declined',
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to decline opportunity')
      }

      setSelectedOpp(null)
      fetchOpportunities() // Refresh
    } catch (err) {
      setError(err.message || 'Failed to decline opportunity')
    } finally {
      setIsResponding(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-hf-teal-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaign Opportunities</h1>
          <p className="text-gray-500">Browse and respond to campaign invitations matched to your profile</p>
        </div>
        <button onClick={fetchOpportunities} className="btn-secondary">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center">
          <CheckCircle className="w-5 h-5 mr-2" />
          {success}
        </div>
      )}

      {/* Filters */}
      <div className="card !p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search opportunities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-hf-teal-500"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'new', 'pending_response'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === f
                    ? 'bg-hf-teal-100 text-hf-teal-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f === 'all' ? 'All' : f === 'new' ? 'New' : 'Pending Response'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Opportunities List */}
      <div className="space-y-4">
        {filteredOpps.map((opp) => (
          <div
            key={opp.id}
            className="card hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => setSelectedOpp(opp)}
          >
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              {/* Brand Info */}
              <div className="flex items-center space-x-4 lg:w-1/4">
                <img
                  src={opp.brand.logo}
                  alt={opp.brand.name}
                  className="w-14 h-14 rounded-xl object-cover"
                />
                <div>
                  <div className="flex items-center">
                    <h3 className="font-semibold text-gray-900">{opp.brand.name}</h3>
                    {opp.brand.verified && (
                      <CheckCircle className="w-4 h-4 text-hf-teal-600 ml-1" />
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{opp.campaignName}</p>
                </div>
              </div>

              {/* Campaign Details */}
              <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Budget</p>
                  <p className="font-medium text-gray-900">{opp.budget}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Deadline</p>
                  <p className="font-medium text-gray-900">
                    {new Date(opp.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Platforms</p>
                  <div className="flex items-center space-x-1">
                    {opp.platforms.map(p => (
                      <span key={p} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded capitalize">
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Deliverables</p>
                  <p className="text-sm text-gray-600">{Array.isArray(opp.deliverables) ? opp.deliverables.join(', ') : opp.deliverables}</p>
                </div>
              </div>

              {/* Match Score & Status */}
              <div className="flex items-center space-x-4 lg:w-auto">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-hf-teal-100 to-teal-100 flex items-center justify-center">
                    <span className="text-lg font-bold text-hf-teal-700">{opp.matchScore}%</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Match</p>
                </div>
                {(opp.status === 'new' || opp.status === 'pending') && (
                  <span className="badge bg-green-100 text-green-700">New</span>
                )}
                {opp.status === 'pending_response' && (
                  <span className="badge bg-yellow-100 text-yellow-700">Pending</span>
                )}
                {opp.status === 'accepted' && (
                  <span className="badge bg-blue-100 text-blue-700">Accepted</span>
                )}
                {opp.status === 'declined' && (
                  <span className="badge bg-gray-100 text-gray-700">Declined</span>
                )}
                {opp.isDemo && (
                  <span className="badge bg-purple-100 text-purple-700 text-xs">Demo</span>
                )}
                {opp.isReal && (
                  <span className="badge bg-green-100 text-green-700 text-xs">Live</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredOpps.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No opportunities found</h3>
          <p className="text-gray-500">Try adjusting your search or check back later for new campaigns.</p>
        </div>
      )}

      {/* Opportunity Detail Modal */}
      {selectedOpp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-gray-900/50"
            onClick={() => setSelectedOpp(null)}
          />
          <div className="relative bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setSelectedOpp(null)}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-6">
              {/* Header */}
              <div className="flex items-start space-x-4 mb-6">
                <img
                  src={selectedOpp.brand.logo}
                  alt={selectedOpp.brand.name}
                  className="w-16 h-16 rounded-xl object-cover"
                />
                <div className="flex-1">
                  <div className="flex items-center mb-1">
                    <h2 className="text-xl font-bold text-gray-900">{selectedOpp.campaignName}</h2>
                    {selectedOpp.isReal && (
                      <span className="badge bg-green-100 text-green-700 text-xs ml-2">Live Opportunity</span>
                    )}
                  </div>
                  <div className="flex items-center text-gray-600">
                    <span>{selectedOpp.brand.name}</span>
                    {selectedOpp.brand.verified && (
                      <CheckCircle className="w-4 h-4 text-hf-teal-600 ml-1" />
                    )}
                  </div>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-hf-teal-100 to-teal-100 flex items-center justify-center">
                    <span className="text-xl font-bold text-hf-teal-700">{selectedOpp.matchScore}%</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Match Score</p>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center text-gray-500 mb-1">
                    <DollarSign className="w-4 h-4 mr-2" />
                    <span className="text-sm">Budget</span>
                  </div>
                  <p className="font-semibold text-gray-900">{selectedOpp.budget}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center text-gray-500 mb-1">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span className="text-sm">Deadline</span>
                  </div>
                  <p className="font-semibold text-gray-900">
                    {new Date(selectedOpp.deadline).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              </div>

              {/* Platforms & Deliverables */}
              <div className="mb-6">
                <h3 className="font-medium text-gray-900 mb-3">Deliverables Required</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedOpp.platforms.map(p => (
                    <span key={p} className="px-3 py-1.5 bg-hf-teal-50 text-hf-teal-700 rounded-full text-sm font-medium capitalize">
                      {p}
                    </span>
                  ))}
                </div>
                <ul className="space-y-2">
                  {(Array.isArray(selectedOpp.deliverables) ? selectedOpp.deliverables : [selectedOpp.deliverables]).map((del, i) => (
                    <li key={i} className="flex items-center text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      {del}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Brief Preview */}
              <div className="mb-6">
                <h3 className="font-medium text-gray-900 mb-3">Campaign Brief</h3>
                <p className="text-gray-600">{selectedOpp.brief}</p>
                <button className="text-hf-teal-600 hover:text-hf-teal-700 text-sm font-medium mt-2 flex items-center">
                  View full brief
                  <ExternalLink className="w-4 h-4 ml-1" />
                </button>
              </div>

              {/* Actions */}
              {(selectedOpp.status === 'new' || selectedOpp.status === 'pending' || selectedOpp.status === 'pending_response') && (
                <div className="flex gap-3 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => handleAccept(selectedOpp)}
                    disabled={isResponding}
                    className="btn-primary flex-1 disabled:opacity-50"
                  >
                    {isResponding ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Accept Opportunity
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleDecline(selectedOpp)}
                    disabled={isResponding}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Decline
                  </button>
                </div>
              )}

              {selectedOpp.status === 'accepted' && (
                <div className="pt-4 border-t border-gray-100">
                  <div className="p-4 bg-green-50 rounded-lg text-green-700 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    You've accepted this opportunity. The brand has been notified.
                  </div>
                </div>
              )}

              {selectedOpp.status === 'declined' && (
                <div className="pt-4 border-t border-gray-100">
                  <div className="p-4 bg-gray-50 rounded-lg text-gray-600 flex items-center">
                    <X className="w-5 h-5 mr-2" />
                    You've declined this opportunity.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CreatorOpportunities
